<template>
  <div>
    <b-form-group
      description="HealthServe is an IPC registered charity. All donations to HealthServe are 250% tax deductible if the relevant details are provided."
      label-size="lg"
      label-cols-md="12"
      label="Tax deduction"
      class="mb-4 tax-deduction"
    >
      <b-form-checkbox
        v-model="enabled"
        :value="false"
        :unchecked-value="true"
        :disabled="ineligible"
        >I am not eligible for tax deductions / I do not require a tax
        deduction.</b-form-checkbox
      >
    </b-form-group>

    <template v-if="enabled">
      <h5
        style="
          font-weight: bold;
          font-size: 18px;
          line-height: 26px;
          color: #231f1f;
          margin-bottom: 16px;
        "
      >
        Tax information
      </h5>

      <b-form-group
        label-cols-sm="6"
        :label="info_label"
        class="mb-4 nrc-fin-num"
        label-class="required"
        :state="data_uen_check"
        invalid-feedback="UEN was not found in database. Check to see if you've typed the UEN correctly. If you're a recently registered entity, or the UEN is correct, you can safely ignore this message."
      >
        <b-input
          :placeholder="info_label"
          required
          v-model="info_data"
          ref="info_data"
          style="text-transform: uppercase"
        />
      </b-form-group>
      <b-form-checkbox v-model="checkout.Tax_Receipt_Email_Copy"
        >I would like to receive a copy of my receipt (via
        email).</b-form-checkbox
      >

      <p style="margin-top: 16px">
        With your name and tax reference number above, tax deduction for your
        donation will be automatically included in your tax assessment. If you
        would like to receive a receipt for donations above $50, please check
        the box to receive one through email - this may take up to 2 working
        days.
      </p>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import debounce from "debounce";

export default {
  name: "tax",
  props: {
    checkout: Object,
  },
  data() {
    return {
      data_uen_check: null,
    };
  },
  mounted() {
    if (this.checkout.Donor_Type == "Corporate") {
      this.validate_uen(this.checkout.Corporate_UEN);
    }
  },
  computed: {
    ineligible() {
      return false;
    },
    enabled: {
      get() {
        return this.checkout.Tax_Deductable_Receipt;
      },
      set(val) {
        this.checkout.Tax_Deductable_Receipt = val;
      },
    },
    info_label() {
      return this.checkout.Donor_Type == "Corporate"
        ? "UEN Number"
        : "NRIC / FIN Number";
    },
    info_data: {
      get() {
        return this.checkout.Donor_Type == "Corporate"
          ? this.checkout.Corporate_UEN
          : this.checkout.Donor_NRIC;
      },
      set(val) {
        if (this.checkout.Donor_Type == "Corporate") {
          this.checkout.Corporate_UEN = val.toUpperCase();
          return;
        }

        this.checkout.Donor_NRIC = val.toUpperCase();
      },
    },
  },
  watch: {
    "checkout.Donor_NRIC"(val) {
      if (!val) {
        this.$refs?.info_data?.setCustomValidity?.("");
        return;
      }

      let isValid = ((nricInput) => {
        // validation rules
        const nricRegex = /(\D)(\d{7})(\D)/;
        const nricTypeRegex = /S|T|F|G|M/;
        const weightArr = [2, 7, 6, 5, 4, 3, 2];
        const nricLetterST = [
          "J",
          "Z",
          "I",
          "H",
          "G",
          "F",
          "E",
          "D",
          "C",
          "B",
          "A",
        ];
        const nricLetterFG = [
          "X",
          "W",
          "U",
          "T",
          "R",
          "Q",
          "P",
          "N",
          "M",
          "L",
          "K",
        ];

        const nricLetterM = [
          "T",
          "R",
          "Q",
          "P",
          "N",
          "M",
          "L",
          "K",
          "X",
          "W",
          "U",
        ];

        // set nric to all uppercase to remove case sensitivity
        const nric = nricInput.toUpperCase();

        // returns false if it false basic validation
        if (!nricRegex.exec(nric)) {
          return false;
        }

        // get nric type
        const nricArr = nric.match(nricRegex);
        const nricType = nricArr[1];

        // returns false if it false basic validation
        if (!nricTypeRegex.exec(nricType)) {
          return false;
        }

        // multiply each digit in the nric number by it's weight in order
        const nricDigitsArr = nricArr[2].split("");

        let total = 0;
        for (let i = 0; i < nricDigitsArr.length; i++) {
          total += parseInt(nricDigitsArr[i]) * weightArr[i];
        }

        // if the nric type is T or G, add 4 to the total
        if (["T", "G"].indexOf(nricType) >= 0) {
          total += 4;
        }

        // check last letter of nric for local
        const letterIndex = total % 11;
        // console.log(letterIndex);

        const nricLetter = nricArr[3];

        if (["S", "T"].indexOf(nricType) >= 0) {
          // console.log(nricLetterST[letterIndex] + "=ST=" + nricLetter);
          return nricLetterST[letterIndex] === nricLetter;
        }

        if (["F", "G"].indexOf(nricType) >= 0) {
          // console.log(nricLetterFG[letterIndex] + "=FG=" + nricLetter);

          // check last letter of nric for foreigners
          return nricLetterFG[letterIndex] === nricLetter;
        }

        return nricLetterM[letterIndex] === nricLetter;
      })(val);

      if (isValid) {
        this.$refs?.info_data.setCustomValidity("");
        return;
      }
      this.$refs?.info_data.setCustomValidity("Please enter a valid NRIC");
    },
    "checkout.Corporate_UEN"(val) {
      this.validate_uen(val);
    },
  },
  methods: {
    validate_uen(val) {
      this.data_uen_check = null;
      if (!val) {
        this.$refs?.info_data?.setCustomValidity?.("");
        return;
      }
      let isValid = ((uen) => {
        var debug = false;
        const entityTypeIndicator = [
          "LP",
          "LL",
          "FC",
          "PF",
          "RF",
          "MQ",
          "MM",
          "NB",
          "CC",
          "CS",
          "MB",
          "FM",
          "GS",
          "GA",
          "GB",
          "DP",
          "CP",
          "NR",
          "CM",
          "CD",
          "MD",
          "HS",
          "VH",
          "CH",
          "MH",
          "CL",
          "XL",
          "CX",
          "RP",
          "TU",
          "TC",
          "FB",
          "FN",
          "PA",
          "PB",
          "SS",
          "MC",
          "SM",
        ];

        if (debug) {
          console.log("(A) Businesses registered with ACRA");
          console.log("(B) Local companies registered with ACRA");
          console.log("(C) All other entities which will be issued new UEN");
        }

        // check that uen is not empty
        if (!uen || String(uen) === "") {
          if (debug) {
            console.log("UEN is empty");
          }
          return false;
        }

        // check if uen is 9 or 10 digits
        if (uen.length < 9 || uen.length > 10) {
          if (debug) {
            console.log("UEN is not 9 or 10 digits");
          }
          return false;
        }

        uen = uen.toUpperCase();
        var uenStrArray = uen.split("");

        // (A) Businesses registered with ACRA
        if (uenStrArray.length === 9) {
          // check that last character is a letter
          if (!isNaN(uenStrArray[uenStrArray.length - 1])) {
            if (debug) {
              console.log("(A) last character is not an alphabet");
            }
            return false;
          }

          for (let i = 0; i < uenStrArray.length - 1; i++) {
            // check that first 8 letters are all numbers
            if (isNaN(uenStrArray[i])) {
              if (debug) {
                console.log("(A) there are non-numbers in 1st to 8th letters");
              }
              return false;
            }
          }

          // (A) Businesses registered with ACRA (SUCCESS)
          if (debug) {
            console.log("valid (A) Businesses registered with ACRA");
          }
          return true;
        } else if (uenStrArray.length === 10) {
          // check that last character is a letter
          if (!isNaN(uenStrArray[uenStrArray.length - 1])) {
            if (debug) {
              console.log("(B)(C) last character is not an alphabet");
            }
            return false;
          }

          // (B) Local companies registered with ACRA
          if (
            !isNaN(uenStrArray[0]) &&
            !isNaN(uenStrArray[1]) &&
            !isNaN(uenStrArray[2]) &&
            !isNaN(uenStrArray[3])
          ) {
            // check that 5th to 9th letters are all numbers
            if (
              !isNaN(uenStrArray[4]) &&
              !isNaN(uenStrArray[5]) &&
              !isNaN(uenStrArray[6]) &&
              !isNaN(uenStrArray[7]) &&
              !isNaN(uenStrArray[8])
            ) {
              // (B) Local companies registered with ACRA (SUCCESS)
              if (debug) {
                console.log("valid (B) Local companies registered with ACRA");
              }
              return true;
            } else {
              if (debug) {
                console.log("(B) there are non-numbers in 5th to 9th letters");
              }
              return false;
            }
          }
          // (C) All other entities which will be issued new UEN
          else {
            // check that 1st letter is either T or S or R
            if (
              uenStrArray[0] !== "T" &&
              uenStrArray[0] !== "S" &&
              uenStrArray[0] !== "R"
            ) {
              if (debug) {
                console.log("(C) 1st letter is incorrect");
              }
              return false;
            }

            // check that 2nd and 3rd letters are numbers only
            if (isNaN(uenStrArray[1]) || isNaN(uenStrArray[2])) {
              if (debug) {
                console.log("(C) 2nd and 3rd letter is incorrect");
              }
              return false;
            }

            // check that 4th letter is an alphabet
            if (!isNaN(uenStrArray[3])) {
              if (debug) {
                console.log("(C) 4th letter is not an alphabet");
              }
              return false;
            }

            // check entity-type indicator
            var entityTypeMatch = false,
              entityType = String(uenStrArray[3]) + String(uenStrArray[4]);
            for (let i = 0; i < entityTypeIndicator.length; i++) {
              if (String(entityTypeIndicator[i]) === String(entityType)) {
                entityTypeMatch = true;
              }
            }
            if (!entityTypeMatch) {
              if (debug) {
                console.log("(C) entity-type indicator is invalid");
              }
              return false;
            }

            // check that 6th to 9th letters are numbers only
            if (
              isNaN(uenStrArray[5]) ||
              isNaN(uenStrArray[6]) ||
              isNaN(uenStrArray[7]) ||
              isNaN(uenStrArray[8])
            ) {
              if (debug) {
                console.log("(C) 2nd and 3rd letter is incorrect");
              }
              return false;
            }

            // (C) All other entities which will be issued new UEN (SUCCESS)
            if (debug) {
              console.log(
                "valid (C) All other entities which will be issued new UEN"
              );
            }
            return true;
          }
        }

        return false;
      })(val);

      if (!isValid) {
        this.$refs?.info_data.setCustomValidity("Please enter a valid UEN");
        return;
      }
      this.$refs?.info_data.setCustomValidity("");

      debounce(
        (uen) => {
          let url = "https://data.gov.sg/api/action/datastore_search";
          let params = {
            resource_id: "39201285-b73e-487a-a971-3a12d34ab8d9", // the resource id
            limit: 1,
            filters: JSON.stringify({
              uen,
              uen_status: "R",
            }),
          };

          axios
            .get(url, { params })
            .then((rsp) => {
              if (!rsp.data.result.records[0]) {
                throw "No Entity Found";
              }
              //managed to get info;
              this.data_uen_check = null;
            })
            .catch(() => {
              params.resource_id = "5ab68aac-91f6-4f39-9b21-698610bdf3f7";
              axios
                .get(url, { params })
                .then((rsp) => {
                  if (!rsp.data.result.records[0]) {
                    throw "No Entity Found";
                  }
                  //managed to get info;
                  this.data_uen_check = null;
                })
                .catch(() => {
                  this.data_uen_check = false;
                });
            });
        },
        800,
        true
      )(val);
    },
  },
};
</script>

<style></style>
